/**
 * Browser-storage keys that belong to a signed-in session rather than to the
 * browser, and so must not survive into the next person's session on a shared
 * machine.
 *
 * Deliberately a leaf module with no service dependencies: both the ordinary
 * sign-out path and the session-ended path need it, and the latter is reached
 * from inside the root data services that the sign-out path itself depends on.
 * Importing a service here would close a module cycle.
 */
const sessionStorageKeys = new Set([
  'dashboard-box-searches:admin:v1',
  'documents.viewMode',
]);

const sessionStorageKeyPrefixes = [
  'dashboard-box-searches:group:',
  'group-summaries-view:',
  'report-filters-',
];

export function clearSessionScopedStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  clearMatchingStorage(window.localStorage);
  clearMatchingStorage(window.sessionStorage);
}

function clearMatchingStorage(storage: Storage): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key) {
      continue;
    }

    if (isSessionScopedKey(key)) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}

export function isSessionScopedKey(key: string): boolean {
  return sessionStorageKeys.has(key)
    || sessionStorageKeyPrefixes.some(prefix => key.startsWith(prefix));
}
